import { MinusIcon, PlusIcon } from "lucide-react";
import {
  Group,
  type InputProps,
  NumberField as RACNumberField,
  type NumberFieldProps as RACNumberFieldProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import { composeTailwindRenderProps } from "@/components/ui/utils";

const numberFieldStyles = tv({
  slots: {
    group: [
      "group isolate grid grid-cols-[auto_auto_1fr_auto_auto]",
      "[&>div:has([role=separator])]:h-full",
      "[&>div:has([role=separator])]:z-10",
      "[&>div:has([role=separator])]:py-[1px]",
      "[&:focus-within>div:has([role=separator])]:py-[2px]",
    ],
    decrementButton:
      "z-10 col-start-1 row-start-1 rounded-none hover:bg-transparent pressed:bg-transparent text-muted-foreground hover:text-foreground",
    input:
      "z-0 col-span-full row-start-1 px-[calc(theme(size.11)+10px)] sm:px-[calc(theme(size.9)+10px)]",
    incrementButton:
      "-col-end-1 row-start-1 rounded-none text-muted-foreground z-20 hover:text-foreground hover:bg-transparent pressed:bg-transparent",
  },
});

const { group, decrementButton, input, incrementButton } = numberFieldStyles();

export interface NumberFieldProps extends RACNumberFieldProps {}

export function NumberField(props: NumberFieldProps) {
  return (
    <RACNumberField
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group *:not-first:mt-1",
      )}
    />
  );
}

export function NumberInput(props: InputProps) {
  return (
    <Group data-ui="control" className={group()}>
      <Button
        slot="decrement"
        size="icon"
        variant="ghost"
        className={decrementButton()}
      >
        <MinusIcon />
      </Button>
      <div className="col-start-2 row-start-1">
        <Separator orientation="vertical" className="h-full" />
      </div>

      <Input
        {...props}
        className={composeTailwindRenderProps(props.className, input())}
      />

      <div className="-col-end-2 row-start-1">
        <Separator orientation="vertical" className="h-full" />
      </div>

      <Button
        slot="increment"
        className={incrementButton()}
        size="icon"
        variant="ghost"
      >
        <PlusIcon />
      </Button>
    </Group>
  );
}
